import React, { useMemo, useState } from 'react';
import { Text, View, StyleSheet } from 'react-native';
import AdminButton from '../../../Admin/components/AdminButton';
import { AdminFormField, AdminTextInput } from '../../../Admin/components/AdminFormField';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import ConfigDrawer from '../components/ConfigDrawer';
import ConfigSummaryBox from '../components/ConfigSummaryBox';
import { DropdownSelect } from '../components/DropdownSelect';
import {
  ConfigHistoryEntry,
  ConfigMetric,
  NextFiscalWeek,
  TargetsMap,
  TargetsSavePayload,
} from '../configTypes';
import {
  formatDateTime,
  formatMetricValue,
  getOwnTarget,
  getPendingTarget,
  locationKey,
  nationalKey,
} from '../configUtils';
import { useAppLocationOptions } from '../useAppLocations';

interface MetricTargetsDrawerProps {
  mode: 'national' | 'location';
  org: string;
  dept: string;
  /** Set when editing an existing override row; absent for "Add Override". */
  startLoc?: string;
  metrics: ConfigMetric[];
  targets: TargetsMap;
  today: string;
  next: NextFiscalWeek;
  history: ConfigHistoryEntry[];
  onClose: () => void;
  onSave: (payload: TargetsSavePayload) => void;
}

const isTarget = (v: string): boolean => Number.isFinite(Number(v)) && Number(v) >= 0;

/**
 * Right side drawer for metric targets. National: every metric needs a value.
 * Location override: a blank keeps the national target. Every change is effective dated.
 */
const MetricTargetsDrawer: React.FC<MetricTargetsDrawerProps> = ({
  mode,
  org,
  dept,
  startLoc,
  metrics,
  targets,
  today,
  next,
  history,
  onClose,
  onSave,
}) => {
  const nationalId = nationalKey(org, dept);
  // Every location in appData.locations (same list as the Onboarding Ramp "Applies to" dropdown).
  const locationOptions = useAppLocationOptions();
  const [loc, setLoc] = useState(startLoc ?? locationOptions[0]?.value ?? '');
  const id = mode === 'national' ? nationalId : locationKey(loc, dept);
  const scope = mode === 'national' ? `${org} ${dept}` : `${loc} / ${dept}`;

  // A saved change that has not taken effect yet is what the drawer edits.
  const load = (rowId: string): Record<string, string> =>
    Object.fromEntries(
      metrics.map((m) => {
        const pending = getPendingTarget(targets, rowId, m.id, today);
        const v = pending ? pending.value : getOwnTarget(targets, rowId, m.id, today);
        return [m.id, v === null || v === undefined ? '' : String(v)];
      })
    );

  const [initial, setInitial] = useState(() => load(id));
  const [form, setForm] = useState(initial);

  const pickLocation = (name: string) => {
    const f = load(locationKey(name, dept));
    setLoc(name);
    setInitial(f);
    setForm(f);
  };

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const valid =
    metrics.every((m) => form[m.id] === '' || isTarget(form[m.id])) &&
    (mode !== 'national' || metrics.every((m) => form[m.id] !== ''));

  const changes = history.filter((h) => h.section === 'Metric targets' && h.scope === scope).slice(0, 5);

  return (
    <ConfigDrawer
      title={mode === 'national' ? 'Edit national targets' : startLoc ? 'Edit location override' : 'Add location override'}
      subtitle={mode === 'national' ? `${org} / ${dept}, every location` : `${loc} / ${dept} (${org})`}
      onClose={onClose}
      footer={
        <>
          <AdminButton label="Cancel" variant="secondary" onPress={onClose} />
          <AdminButton
            label="Save Targets"
            variant="primary"
            disabled={!dirty || !valid}
            onPress={() => onSave({ id, scope, nationalId, form, initial, metrics })}
          />
        </>
      }
    >
      {mode === 'location' && !startLoc ? (
        <AdminFormField label="Location">
          <DropdownSelect value={loc} onChange={pickLocation} options={locationOptions} placeholder="Select location" />
        </AdminFormField>
      ) : null}
      {mode === 'location' ? <Text style={styles.note}>Leave a metric blank to keep the national target.</Text> : null}

      {metrics.map((m) => {
        const national = mode === 'location' ? getOwnTarget(targets, nationalId, m.id, today) : null;
        const invalid = form[m.id] !== '' && !isTarget(form[m.id]);
        return (
          <AdminFormField
            key={m.id}
            label={`${m.name} target`}
            hint={`${m.unit ? `${m.unit}, ` : ''}${m.direction === 'lower' ? 'lower is better' : 'higher is better'}`}
            error={invalid ? 'Enter a number of 0 or more.' : undefined}
          >
            <AdminTextInput
              keyboardType="decimal-pad"
              value={form[m.id] ?? ''}
              hasError={invalid}
              placeholder={national !== null ? `National ${formatMetricValue(m, national)}` : ''}
              onChangeText={(t) => setForm({ ...form, [m.id]: t.trim() })}
            />
          </AdminFormField>
        );
      })}

      <ConfigSummaryBox
        label="Takes effect"
        value={`Start of the next fiscal week, ${next.display}`}
        note="Earlier weeks keep the targets they had."
      />

      <View style={styles.changes}>
        <Text style={styles.sectionTitle}>Changes here</Text>
        {changes.length ? (
          changes.map((h) => (
            <Text key={`${h.at}-${h.setting}`} style={styles.change}>
              <Text style={styles.changeStrong}>{h.setting}</Text>
              {`: ${h.prior} to ${h.next}, effective ${h.effective}. ${h.by}, ${formatDateTime(h.at)}.`}
            </Text>
          ))
        ) : (
          <Text style={styles.note}>No target changes here yet.</Text>
        )}
      </View>
    </ConfigDrawer>
  );
};

const styles = StyleSheet.create({
  note: { fontSize: 12, color: AdminColors.textMuted, fontFamily: FontFamily.regular, marginBottom: 12 },
  changes: { marginTop: 8, gap: 6 },
  sectionTitle: { fontSize: 13, color: AdminColors.textPrimary, fontFamily: FontFamily.bold },
  change: { fontSize: 12, color: AdminColors.textSecondary, fontFamily: FontFamily.regular, lineHeight: 17 },
  changeStrong: { fontFamily: FontFamily.bold, color: AdminColors.textPrimary },
});

export default MetricTargetsDrawer;
